import React, { useMemo, useState } from 'react';
import { Button, message, Skeleton, Space, Table, Tag } from 'antd';
import { BarChartOutlined, DownOutlined, TeamOutlined, UpOutlined, UserOutlined } from '@ant-design/icons';
import type { ColumnsType, ColumnType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import TablePaginationBar from '../../../../../components/TablePaginationBar';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
} from '../../../../../components/asyncReportDownload';
import KpiCardWithWow from './KpiCardWithWow';
import HourlyTrendChart from './HourlyTrendChart';
import GroupComparisonChart from './GroupComparisonChart';
import BaggingDrilldownPanel from './BaggingDrilldownPanel';
import SortingDetailDrawer from './SortingDetailDrawer';
import type { InhubSortingMetric } from '../graphql/queries/getInhubSortingDetailPage';
import {
    GET_INHUB_SORTING_KPI_SUMMARY,
    type GetInhubSortingKpiSummaryResponse
} from '../graphql/queries/getInhubSortingKpiSummary';
import {
    GET_INHUB_SORTING_ACCOUNT_SUMMARIES_PAGE,
    type GetInhubSortingAccountSummariesPageResponse,
    type InhubSortingAccountOrderField,
    type InhubSortingAccountRowResp,
    type InhubSortingDimension,
    type SortDirection
} from '../graphql/queries/getInhubSortingAccountSummariesPage';
import {
    GENERATE_INHUB_SORTING_ACCOUNT_SUMMARIES_EXPORT,
    type GenerateInhubSortingAccountSummariesExportResponse,
    type GenerateInhubSortingAccountSummariesExportVariables
} from '../graphql/mutations/generateInhubSortingAccountSummariesExport';
import {
    KEY_TO_METRIC,
    METRIC_TO_KEY,
    type InhubKpiCell,
    type SortingMetricKey
} from '../graphql/types';

const I18N_BASE = 'wms.delivery.inhubDashboard.sorting';
const DEFAULT_PAGE_SIZE = 10;

const COLUMN_TO_ORDER_FIELD: Record<string, InhubSortingAccountOrderField> = {
    accountName: 'ACCOUNT_NAME',
    laborTeamName: 'LABOR_TEAM_NAME',
    accountCount: 'ACCOUNT_COUNT',
    inboundCount: 'INBOUND_COUNT',
    baggingCount: 'BAGGING_COUNT',
    loadingParcelCount: 'LOADING_PARCEL_COUNT',
    wrongRouteBaggingCount: 'WRONG_ROUTE_BAGGING_COUNT',
    wrongSortCount: 'WRONG_SORT_COUNT',
    ascanMissCount: 'ASCAN_MISS_COUNT',
    transitInMissCount: 'TRANSIT_IN_MISS_COUNT'
};

const EMPTY_CELL: InhubKpiCell = {
    value: 0,
    wowPercent: 0,
    freshness: 'REALTIME',
    severity: 'NONE',
    isNegativeIndicator: false
};

interface Props {
    hubId: number;
    rangeStart: string;
    rangeEnd: string;
    isTailHub: boolean;
}

const SortingManagementTab: React.FC<Props> = ({ hubId, rangeStart, rangeEnd, isTailHub }) => {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [drilldownOpen, setDrilldownOpen] = useState(false);
    const [selectedKpiKey, setSelectedKpiKey] = useState<SortingMetricKey>('inboundCount');
    const [orderBy, setOrderBy] = useState<InhubSortingAccountOrderField>('INBOUND_COUNT');
    const [orderDir, setOrderDir] = useState<SortDirection>('DESC');
    const [dimension, setDimension] = useState<InhubSortingDimension>('ACCOUNT');
    const [detailDrawer, setDetailDrawer] = useState<{
        open: boolean;
        metric: InhubSortingMetric;
        groupKey: number | null;
        accountName: string | null;
        laborTeamName: string | null;
        title: string;
        // KPI 卡数字下钻走全量（true）；分组行下钻按 groupKey/未分组桶（false）
        matchAllGroups: boolean;
    }>({
        open: false,
        metric: 'INBOUND_COUNT',
        groupKey: null,
        accountName: null,
        laborTeamName: null,
        title: '',
        matchAllGroups: false,
    });

    const openKpiDrilldown = (metric: InhubSortingMetric, metricLabel: string) => {
        setDetailDrawer({
            open: true,
            metric,
            groupKey: null,
            accountName: null,
            laborTeamName: null,
            title: metricLabel,
            matchAllGroups: true,
        });
    };

    const openDetailDrawer = (
        metric: InhubSortingMetric,
        row: InhubSortingAccountRowResp,
        metricLabel: string
    ) => {
        const key = dimension === 'TEAM' ? row.laborTeamId : row.accountId;
        // 后端汇总侧对 UNGROUPED 桶（NULL/AWO 查不到/AWO 空 name）出参映射为空串，
        // 前端在标题拼接场景空值 fallback 到本地 i18n 标签，避免"未分组"字面量在前端硬编码
        const groupName =
            dimension === 'TEAM'
                ? (row.laborTeamName || (unGrouped as string))
                : (row.accountName || '');
        // ACCOUNT 维度：数仓 accountId → accountName 是多对多，必须把该行的具体 name 一起下钻，
        // 否则明细会混入同 id 下其他 name。TEAM 维度用 accountName 过滤，置 null。
        const nameForDrill =
            dimension === 'ACCOUNT' && row.accountName ? row.accountName : null;
        // TEAM 维度：汇总层按 laborTeamName 合并同名的多 laborTeamId，合并行 laborTeamId=null / groupKey 传不下去；
        // 原样透传 laborTeamName（UNGROUPED 桶为空串 → null），后端按 isNullOrBlank 分流具体队伍/未分组桶。
        const teamNameForDrill =
            dimension === 'TEAM' ? (row.laborTeamName || null) : null;
        setDetailDrawer({
            open: true,
            metric,
            groupKey: key ?? null,
            accountName: nameForDrill,
            laborTeamName: teamNameForDrill,
            title: `${groupName} - ${metricLabel}`,
            matchAllGroups: false,
        });
    };

    const renderClickableNumber = (
        metric: InhubSortingMetric,
        metricLabel: string,
    ) => (value: number, row: InhubSortingAccountRowResp) => {
        if (value <= 0) {
            return <span>{Number(value).toLocaleString()}</span>;
        }
        return (
            <Button
                type="link"
                size="small"
                className="p-0 h-auto"
                onClick={() => openDetailDrawer(metric, row, metricLabel)}
            >
                {Number(value).toLocaleString()}
            </Button>
        );
    };

    const handleSelectKpi = (key: SortingMetricKey) => {
        setSelectedKpiKey(key);
        if (key !== 'baggingCount') {
            setDrilldownOpen(false);
        }
    };

    const { data: kpiData, loading: kpiLoading } = useQuery<GetInhubSortingKpiSummaryResponse>(
        GET_INHUB_SORTING_KPI_SUMMARY,
        {
            variables: { filter: { hubId, rangeStart, rangeEnd } },
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true
        }
    );

    // 后端 6 个 KPI cell 转成 key 索引的 Map，缺失指标回退到 0
    const kpiByKey: Record<SortingMetricKey, InhubKpiCell> = useMemo(() => {
        const result: Record<SortingMetricKey, InhubKpiCell> = {
            inboundCount: EMPTY_CELL,
            baggingCount: EMPTY_CELL,
            loadingParcelCount: EMPTY_CELL,
            wrongRouteBaggingCount: EMPTY_CELL,
            wrongSortCount: EMPTY_CELL,
            ascanMissCount: EMPTY_CELL,
            transitInMissCount: EMPTY_CELL
        };
        const cells = kpiData?.getInhubSortingKpiSummary?.cells ?? [];
        cells.forEach((c) => {
            const key = METRIC_TO_KEY[c.metric];
            if (key) {
                result[key] = {
                    value: c.value,
                    wowPercent: c.wowPercent,
                    freshness: c.freshness,
                    severity: c.severity,
                    isNegativeIndicator: c.isNegativeIndicator
                };
            }
        });
        return result;
    }, [kpiData]);

    const calculatedAtIso = kpiData?.getInhubSortingKpiSummary?.dataCalculatedAtMax ?? null;
    const calculatedAtLabel = calculatedAtIso
        ? dayjs(calculatedAtIso).format('YYYY-MM-DD HH:mm:ss')
        : '-';

    const thresholds = kpiData?.getInhubSortingKpiSummary?.thresholds;
    const warnPct = thresholds?.warnWowPercent;
    const dangerPct = thresholds?.dangerWowPercent;

    const baggingFooter = (
        <div
            className="flex items-center justify-between text-purple-600 text-xs font-medium"
            onClick={() => setDrilldownOpen((v) => !v)}
        >
            <span className="flex items-center gap-1.5">
                <BarChartOutlined />
                {t('wms.delivery.inhubDashboard.bagging.expandByDest')}
            </span>
            {drilldownOpen ? <UpOutlined /> : <DownOutlined />}
        </div>
    );

    const kpiList: { label: string; key: SortingMetricKey; definitionTooltip?: React.ReactNode; footer?: React.ReactNode }[] = useMemo(() => [
        { label: t(`${I18N_BASE}.indicators.inboundCount`), key: 'inboundCount', definitionTooltip: t(`${I18N_BASE}.indicatorDefs.inboundCount`) },
        { label: t(`${I18N_BASE}.indicators.baggingCount`), key: 'baggingCount', definitionTooltip: t(`${I18N_BASE}.indicatorDefs.baggingCount`), footer: isTailHub ? baggingFooter : undefined },
        { label: t(`${I18N_BASE}.indicators.loadingParcelCount`), key: 'loadingParcelCount', definitionTooltip: t(`${I18N_BASE}.indicatorDefs.loadingParcelCount`) },
        { label: t(`${I18N_BASE}.indicators.wrongRouteBaggingCount`), key: 'wrongRouteBaggingCount', definitionTooltip: t(`${I18N_BASE}.indicatorDefs.wrongRouteBaggingCount`) },
        { label: t(`${I18N_BASE}.indicators.wrongSortCount`), key: 'wrongSortCount', definitionTooltip: t(`${I18N_BASE}.indicatorDefs.wrongSortCount`) },
        { label: t(`${I18N_BASE}.indicators.ascanMissCount`), key: 'ascanMissCount', definitionTooltip: t(`${I18N_BASE}.indicatorDefs.ascanMissCount`) },
        { label: t(`${I18N_BASE}.indicators.transitInMissCount`), key: 'transitInMissCount', definitionTooltip: t(`${I18N_BASE}.indicatorDefs.transitInMissCount`) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [t, drilldownOpen, isTailHub]);

    const selectedKpiLabel: string = kpiList.find((k) => k.key === selectedKpiKey)?.label ?? kpiList[0].label;
    const selectedMetric = KEY_TO_METRIC[selectedKpiKey];

    const unGrouped = t('wms.delivery.inhubDashboard.unGrouped');

    const t1Tag = (
        <Tag color="orange" className="ml-1 m-0 text-xs" bordered={false}>
            {t('wms.delivery.inhubDashboard.badge.t1')}
        </Tag>
    );

    const sortOrderOf = (
        field: InhubSortingAccountOrderField
    ): ColumnType<InhubSortingAccountRowResp>['sortOrder'] => {
        if (orderBy !== field) return null;
        return orderDir === 'ASC' ? 'ascend' : 'descend';
    };

    const accountColumn: ColumnType<InhubSortingAccountRowResp> = {
        title: t(`${I18N_BASE}.columns.account`),
        dataIndex: 'accountName',
        width: 160,
        fixed: 'left' as const,
        align: 'left' as const,
        sorter: true,
        sortOrder: sortOrderOf('ACCOUNT_NAME')
    };
    const laborTeamColumn: ColumnType<InhubSortingAccountRowResp> = {
        title: t(`${I18N_BASE}.columns.laborTeam`),
        dataIndex: 'laborTeamName',
        width: 130,
        fixed: dimension === 'TEAM' ? ('left' as const) : undefined,
        align: 'left' as const,
        sorter: true,
        sortOrder: sortOrderOf('LABOR_TEAM_NAME'),
        render: (v: string) => v
            ? v
            : <span className="text-gray-400">{unGrouped}</span>
    };
    const accountCountColumn: ColumnType<InhubSortingAccountRowResp> = {
        title: t(`${I18N_BASE}.columns.accountCount`),
        dataIndex: 'accountCount',
        width: 90,
        align: 'left' as const,
        sorter: true,
        sortOrder: sortOrderOf('ACCOUNT_COUNT'),
        render: (v: number) => Number(v ?? 0).toLocaleString()
    };

    const inboundLabel = t(`${I18N_BASE}.indicators.inboundCount`) as string;
    const baggingLabel = t(`${I18N_BASE}.indicators.baggingCount`) as string;
    const wrongRouteLabel = t(`${I18N_BASE}.columns.wrongRouteShort`) as string;
    const loadingLabel = t(`${I18N_BASE}.indicators.loadingParcelCount`) as string;
    const wrongSortLabel = t(`${I18N_BASE}.columns.wrongSortShort`) as string;
    const ascanMissLabel = t(`${I18N_BASE}.indicators.ascanMissCount`) as string;
    const transitInMissLabel = t(`${I18N_BASE}.columns.transitInMissShort`) as string;

    const columns: ColumnsType<InhubSortingAccountRowResp> = [
        ...(dimension === 'TEAM' ? [] : [accountColumn]),
        laborTeamColumn,
        ...(dimension === 'TEAM' ? [accountCountColumn] : []),
        {
            title: inboundLabel,
            dataIndex: 'inboundCount',
            width: 130,
            align: 'left' as const,
            sorter: true,
            sortOrder: sortOrderOf('INBOUND_COUNT'),
            render: renderClickableNumber('INBOUND_COUNT', inboundLabel)
        },
        {
            title: baggingLabel,
            dataIndex: 'baggingCount',
            width: 100,
            align: 'left' as const,
            sorter: true,
            sortOrder: sortOrderOf('BAGGING_COUNT'),
            render: renderClickableNumber('BAGGING_COUNT', baggingLabel)
        },
        {
            title: wrongRouteLabel,
            dataIndex: 'wrongRouteBaggingCount',
            width: 100,
            align: 'left' as const,
            sorter: true,
            sortOrder: sortOrderOf('WRONG_ROUTE_BAGGING_COUNT'),
            render: renderClickableNumber('WRONG_ROUTE_BAGGING_COUNT', wrongRouteLabel)
        },
        {
            title: loadingLabel,
            dataIndex: 'loadingParcelCount',
            width: 110,
            align: 'left' as const,
            sorter: true,
            sortOrder: sortOrderOf('LOADING_PARCEL_COUNT'),
            render: renderClickableNumber('LOADING_PARCEL_COUNT', loadingLabel)
        },
        {
            title: <span className="inline-flex items-center">{wrongSortLabel}{t1Tag}</span>,
            dataIndex: 'wrongSortCount',
            width: 180,
            align: 'left' as const,
            sorter: true,
            sortOrder: sortOrderOf('WRONG_SORT_COUNT'),
            render: renderClickableNumber('WRONG_SORT_COUNT', wrongSortLabel)
        },
        {
            title: <span className="inline-flex items-center">{ascanMissLabel}{t1Tag}</span>,
            dataIndex: 'ascanMissCount',
            width: 180,
            align: 'left' as const,
            sorter: true,
            sortOrder: sortOrderOf('ASCAN_MISS_COUNT'),
            render: renderClickableNumber('ASCAN_MISS_COUNT', ascanMissLabel)
        },
        {
            title: <span className="inline-flex items-center">{transitInMissLabel}{t1Tag}</span>,
            dataIndex: 'transitInMissCount',
            width: 160,
            align: 'left' as const,
            sorter: true,
            sortOrder: sortOrderOf('TRANSIT_IN_MISS_COUNT'),
            render: renderClickableNumber('TRANSIT_IN_MISS_COUNT', transitInMissLabel)
        }
    ];

    const handleTableChange = (
        _pagination: TablePaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter: SorterResult<InhubSortingAccountRowResp> | SorterResult<InhubSortingAccountRowResp>[]
    ) => {
        const single = Array.isArray(sorter) ? sorter[0] : sorter;
        const field = typeof single?.field === 'string' ? single.field : undefined;
        const nextField = field ? COLUMN_TO_ORDER_FIELD[field] : undefined;
        if (!nextField || !single?.order) {
            setOrderBy('INBOUND_COUNT');
            setOrderDir('DESC');
        } else {
            setOrderBy(nextField);
            setOrderDir(single.order === 'ascend' ? 'ASC' : 'DESC');
        }
        setPage(1);
    };

    // network-only：每次 dimension / 翻页 / 排序变化均从网络重取，不读旧缓存，
    // 避免 Apollo InMemoryCache 在 accountId 不唯一时将 TEAM 数据累积写入 ACCOUNT 缓存槽
    const { data: accountData, loading: accountLoading } = useQuery<GetInhubSortingAccountSummariesPageResponse>(
        GET_INHUB_SORTING_ACCOUNT_SUMMARIES_PAGE,
        {
            variables: { filter: { hubId, rangeStart, rangeEnd, page, pageSize, orderBy, orderDir, dimension } },
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true
        }
    );

    const accountRows = accountData?.getInhubSortingAccountSummariesPage?.content ?? [];
    const accountTotal = accountData?.getInhubSortingAccountSummariesPage?.totalElements ?? 0;

    const [exportMutation, { loading: exportLoading }] = useMutation<
        GenerateInhubSortingAccountSummariesExportResponse,
        GenerateInhubSortingAccountSummariesExportVariables
    >(GENERATE_INHUB_SORTING_ACCOUNT_SUMMARIES_EXPORT);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');

    const handleExport = async () => {
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const result = await exportMutation({
                variables: {
                    filter: { hubId, rangeStart, rangeEnd, page: 1, pageSize: 1, orderBy, orderDir, dimension },
                    timezone: tz
                }
            });
            const fileName = result.data?.generateInhubSortingAccountSummariesExport;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadModalVisible(true);
            }
        } catch (e: unknown) {
            const errMsg = e instanceof Error ? e.message : String(e);
            message.error(errMsg);
        }
    };

    const realtimeKpis = kpiList.slice(0, 4);
    const t1Kpis = kpiList.slice(4);

    return (
        <div className="flex flex-col gap-4">
            {/* 第一行:实时指标 4 个 × 25% */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {realtimeKpis.map((item) => (
                    <KpiCardWithWow
                        key={item.key}
                        label={item.label}
                        definitionTooltip={item.definitionTooltip}
                        cell={kpiByKey[item.key]}
                        onClick={() => handleSelectKpi(item.key)}
                        onDrilldown={() => openKpiDrilldown(KEY_TO_METRIC[item.key], item.label)}
                        selected={selectedKpiKey === item.key}
                        footer={item.footer}
                        loading={kpiLoading}
                    />
                ))}
            </div>

            {isTailHub && (
                <BaggingDrilldownPanel
                    open={drilldownOpen}
                    onClose={() => setDrilldownOpen(false)}
                    hubId={hubId}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                />
            )}

            {/* 第二行:T+1 指标 3 个 × 33% */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {t1Kpis.map((item) => (
                    <KpiCardWithWow
                        key={item.key}
                        label={item.label}
                        definitionTooltip={item.definitionTooltip}
                        cell={kpiByKey[item.key]}
                        onClick={() => handleSelectKpi(item.key)}
                        onDrilldown={() => openKpiDrilldown(KEY_TO_METRIC[item.key], item.label)}
                        selected={selectedKpiKey === item.key}
                        footer={item.footer}
                        loading={kpiLoading}
                    />
                ))}
            </div>

            {warnPct != null && dangerPct != null && (
                <p className="text-black mb-2">
                    {t('wms.delivery.inhubDashboard.kpi.thresholdHint', { warn: warnPct, danger: dangerPct })}。
                </p>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <HourlyTrendChart
                    hubId={hubId}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                    metric={selectedMetric}
                    metricLabel={selectedKpiLabel}
                />
                <GroupComparisonChart
                    hubId={hubId}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                    metric={selectedMetric}
                    metricLabel={selectedKpiLabel}
                />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                    <Space>
                        <Button
                            type={dimension === 'ACCOUNT' ? 'primary' : 'default'}
                            icon={<UserOutlined />}
                            onClick={() => { setDimension('ACCOUNT'); setPage(1); }}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t(`${I18N_BASE}.dimension.byAccount`)}</span>
                        </Button>
                        <Button
                            type={dimension === 'TEAM' ? 'primary' : 'default'}
                            icon={<TeamOutlined />}
                            onClick={() => { setDimension('TEAM'); setPage(1); }}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t(`${I18N_BASE}.dimension.byTeam`)}</span>
                        </Button>
                    </Space>
                    {calculatedAtIso && (
                        <span className="text-sm text-red-500 font-bold">
                            {t('wms.delivery.inhubDashboard.dataUpdatedAt')} {calculatedAtLabel}
                        </span>
                    )}
                </div>
                <Button
                    type="primary"
                    loading={exportLoading}
                    onClick={handleExport}
                    disabled={accountTotal === 0}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t(`${I18N_BASE}.export.button`)}</span>
                </Button>
            </div>

            {accountLoading && accountRows.length === 0 ? (
                <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
                <Table<InhubSortingAccountRowResp>
                    rowKey={(r) => (dimension === 'TEAM' ? `team-${r.laborTeamId ?? 'none'}` : `${r.accountId}-${r.laborTeamId ?? 'none'}`)}
                    columns={columns}
                    dataSource={accountRows}
                    pagination={false}
                    size="middle"
                    scroll={{ x: 1000 }}
                    loading={accountLoading}
                    onChange={handleTableChange}
                    showSorterTooltip={false}
                />
            )}
            <p className="text-black mb-2">
                {t(`${I18N_BASE}.tableHint`)}
            </p>
            <TablePaginationBar
                total={accountTotal}
                current={page}
                pageSize={pageSize}
                onChange={(p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                }}
                onShowSizeChange={(_, ps) => {
                    setPage(1);
                    setPageSize(ps);
                }}
            />

            <SortingDetailDrawer
                open={detailDrawer.open}
                onClose={() => setDetailDrawer((s) => ({ ...s, open: false }))}
                hubId={hubId}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                dimension={dimension}
                groupKey={detailDrawer.groupKey}
                accountName={detailDrawer.accountName}
                laborTeamName={detailDrawer.laborTeamName}
                metric={detailDrawer.metric}
                title={detailDrawer.title}
                matchAllGroups={detailDrawer.matchAllGroups}
            />

            <AsyncReportDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.inhubSorting}
            />
        </div>
    );
};

export default SortingManagementTab;
